import { Box, Button, Dialog, Stack, SvgIcon, Typography } from '@mui/material';
import { getColor } from '../../assets/theme';
import Image from '../ui/image';
import GuideSvg from '../../assets/guide-svg.svg';
import { SvgIconComponent } from '@mui/icons-material';
import { guideTitleType, setGuideState } from '../../store/slices/guide.slice';
import { useDispatch } from 'react-redux';
import useLayoutContext from '../../hooks/useLayoutContext';
import { ReactNode, useEffect } from 'react';

/**
 * DialogGuide Component
 *
 * This component renders a dialog box that serves as a guide or tutorial for the user.
 * It displays a welcome message, a title, an icon, and buttons for user interaction.
 *
 * @param {Object} props - The properties passed to the component
 * @param {Object} props.guideDisclosure - An object containing methods to control the dialog's visibility
 * @param {boolean} props.guideDisclosure.isOpen - Determines if the dialog is open
 * @param {Function} props.guideDisclosure.onOpen - Function to open the dialog
 * @param {Function} props.guideDisclosure.onClose - Function to close the dialog
 * @param {Function} props.guideDisclosure.onToggle - Function to toggle the dialog's visibility
 * @param {Function} props.handleClick - Function to handle the "Get Started" button click
 * @param {string} props.title - The title of the guide
 * @param {SvgIconComponent} props.icon - The icon to display in the guide
 * @param {guideTitleType} props.dontShowAgain - The type of guide for the "Don't Show Again" functionality
 */
const DialogGuide = ({
  guideDisclosure,
  handleClick,
  title,
  icon,
  dontShowAgain,
  desc,
  highlightDesc,
  welcomeMessage,
  customButton,
}: {
  guideDisclosure: {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    onToggle: () => void;
  };
  handleClick: () => void;
  title: string;
  icon: SvgIconComponent;
  dontShowAgain: guideTitleType;
  desc?: string | ReactNode;
  highlightDesc?: string;
  welcomeMessage?: string;
  customButton?: ReactNode;
}) => {
  const { primary } = getColor();
  const dispatch = useDispatch();

  const { openModuleGuide, handleOpenModuleGuide } = useLayoutContext();

  // Effect to open the guide when openModuleGuide matches dontShowAgain
  useEffect(() => {
    if (!openModuleGuide || openModuleGuide !== dontShowAgain) return;
    guideDisclosure.onOpen();
  }, [openModuleGuide]);

  return (
    <Dialog open={guideDisclosure.isOpen} maxWidth='xl'>
      <Stack sx={{ p: '44px' }} gap={{ xs: 3, md: 0 }}>
        <Stack
          direction={{ xs: 'column-reverse', md: 'row' }}
          gap={{ xs: 4, md: '50px' }}
        >
          <Stack justifyContent='center'>
            <Stack direction='row' gap='16px' alignItems='center' pb='24px'>
              <Box
                sx={{
                  bgcolor: primary,
                  padding: '14px',
                  width: '62px',
                  height: '62px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SvgIcon
                  component={icon}
                  sx={{ fontSize: 33, color: '#FFFFFF' }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: '20px',
                  fontWeight: 600,
                  letterSpacing: '.15px',
                }}
              >
                {title}
              </Typography>
            </Stack>
            <Stack
              sx={{
                border: `1px solid ${primary}`,
                width: 'fit-content',
                borderRadius: '8px',
                p: '4px 12px',
                justifyContent: 'center',
                alignItems: 'center',
                mb: '8px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  letterSpacing: '.1px',
                  color: primary,
                }}
              >
                {welcomeMessage || 'Welcome!'}
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontSize: '34px',
                fontWeight: 600,
                letterSpacing: '.25px',
                lineHeight: '123.5%',
                mb: '8px',
              }}
            >
              {highlightDesc || "Let's start with a quick tour"}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontSize: '16px',
                fontWeight: 400,
                letterSpacing: '.15px',
              }}
            >
              {desc || "We'll have you up and running in no time."}
            </Typography>
          </Stack>
          <Stack alignItems='center'>
            <Image
              img={GuideSvg}
              width={{ xs: 140, md: 230 }}
              height={'auto'}
            />
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          flexWrap='wrap'
          gap={2}
          width='100%'
        >
          {customButton || (
            <>
              <Button variant='contained' onClick={handleClick}>
                Get Started
              </Button>
              <Button
                variant='outlined'
                onClick={() => {
                  guideDisclosure.onClose();
                  handleOpenModuleGuide(null);
                }}
              >
                Show Me Later
              </Button>
              <Button
                variant='outlined'
                onClick={() => {
                  dontShowAgain &&
                    dispatch(setGuideState({ [dontShowAgain]: true }));
                  guideDisclosure.onClose();
                  handleOpenModuleGuide(null);
                }}
              >
                Don't Show Again
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default DialogGuide;
